/**
 * The cross-period amendment tables for the period on screen (finding A2,
 * stage 5e; e2e-filing B-05/B-06) — every amendment table (B2BA, B2CLA, EXPA,
 * B2CSA, CDNRA, CDNURA, ATA, TXPA, 14A) against an EARLIER month's filed return.
 *
 * Building is no longer read-only: since B-06 a build is KEPT on this period, and
 * this period's utility workbook and GSTN JSON write it. The card says so, and
 * that building against another month replaces the build.
 *
 * Four things on it are claims, and each is said rather than implied:
 * a filed document missing from our books is LISTED and never zeroed (removing
 * a filed invoice is a decision, not an amendment); a table whose base table
 * this app does not book (EXPA, ATA, TXPA today) is NOT BUILT rather than shown
 * empty; where the filed return came from — the stored month or a file in hand —
 * because an amendment read later is only as good as that; and what the outputs
 * now do with the build.
 */
import { Alert, Button, Card } from '@fiveox/ui'
import { useState } from 'react'

import {
  amendmentHeadline,
  keptSentence,
  originalCandidates,
  TABLE_LABELS,
  unbuiltSentences,
} from '../lib/amendments'
import {
  type AmendmentRow,
  type AmendmentsBody,
  type AmendmentTable,
  ApiError,
  type PeriodRecord,
  periods,
} from '../lib/api'

const HEADS = ['taxable', 'igst', 'cgst', 'sgst', 'cess'] as const

/** Who or where a row is: the customer's GSTIN, the operator's, or the place of supply
 *  where there is no counterparty (B2CLA, CDNURA, the consolidated tables). */
function party(r: AmendmentRow): string {
  if (r.operator_name) return r.ctin ? `${r.operator_name} · ${r.ctin}` : r.operator_name
  return r.ctin ?? (r.pos ? `Place of supply ${r.pos}` : '—')
}

/** The document, or for a consolidated row the rate it restates. */
function documentOf(r: AmendmentRow): string {
  if (r.original_doc_no) return r.doc_no ?? '—'
  if (r.rate !== undefined && r.rate !== null) return `${r.rate}% for ${r.original_period}`
  return r.doc_no ?? '—'
}

/** Indian grouping, and a sign on a difference — as Prepared vs Filed shows it. */
function rupees(paise: number | undefined | null, signed = false): string {
  if (paise === null || paise === undefined) return '—'
  const value = paise / 100
  const text = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  if (!signed) return value < 0 ? `(${text})` : text
  if (value === 0) return '0.00'
  return value > 0 ? `+${text}` : `−${text}`
}

function Rows({ rows }: { rows: AmendmentRow[] }) {
  return (
    <div className="gst-table-wrap mb-4">
      <table className="gst-table">
        <thead>
          <tr>
            <th className="is-text">Customer, operator or place</th>
            <th className="is-text">Document</th>
            <th className="is-text">Date</th>
            <th>Filed taxable</th>
            <th>Revised taxable</th>
            {HEADS.map((h) => (
              <th key={h}>Δ {h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.ctin}|${r.pos}|${r.doc_no}`} className={i % 2 ? 'is-a' : undefined}>
              <td className="is-text">
                <span className="gst-mono">{party(r)}</span>
              </td>
              <td className="is-text">{documentOf(r)}</td>
              <td className="is-text">{r.doc_date ?? '—'}</td>
              <td>{rupees(r.filed.taxable)}</td>
              <td>{rupees(r.revised.taxable)}</td>
              {HEADS.map((h) => (
                <td key={h}>{rupees(r.diff[h], true)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function AmendmentsCard({ period, rows }: { period: PeriodRecord; rows: PeriodRecord[] }) {
  const candidates = originalCandidates(rows, period)
  const [original, setOriginal] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [body, setBody] = useState<AmendmentsBody | null>(null)

  const build = async () => {
    setBusy(true)
    setError(null)
    setBody(null)
    try {
      setBody(
        (
          await periods.amendments(period.id, {
            originalPeriod: original || undefined,
            file: file ?? undefined,
          })
        ).data,
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The amendments could not be built.')
    } finally {
      setBusy(false)
    }
  }

  const result = body?.amendments

  return (
    <Card className="mt-6 p-4">
      <p className="gst-panel-h mb-1">Amendments to an earlier month</p>
      <p className="gst-meta mb-3">
        What this period&rsquo;s books now say about documents already filed in an earlier
        month — every amendment table, B2BA to 14A. Choose the month, or upload its filed
        return (<code>.json</code>). The build is kept on this period, and its utility workbook
        and GSTN JSON write it. A correction to <em>this</em> month after its GSTR-1 is filed is
        a GSTR-1A, not an amendment: the filed month&rsquo;s panel offers{' '}
        <em>Open a GSTR-1A for this month</em>, and the 1A sits in the list beside it.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={original}
          onChange={(e) => setOriginal(e.target.value)}
          className="gst-input"
          aria-label="The month being amended"
        >
          <option value="">
            {candidates.length > 0 ? 'Choose a filed month…' : 'No earlier filed month held'}
          </option>
          {candidates.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <input
          type="file"
          accept="application/json,.json"
          aria-label="Or the filed return (.json)"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="gst-input"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || (!original && !file)}
          onClick={() => void build()}
        >
          {busy ? 'Building…' : 'Build the amendments'}
        </Button>
      </div>

      {error && (
        <Alert tone="error" className="mt-3">
          {error}
        </Alert>
      )}

      {body && result && (
        <div className="mt-4">
          <p className="text-sm text-foreground">{amendmentHeadline(result)}</p>
          <p className="gst-meta mb-3">
            Against the filed return {body.filed_source === 'stored' ? 'held for that month' : 'uploaded here'}
            {result.convention ? ` · ${result.convention}` : ''}
          </p>

          {result.applicable &&
            Object.entries(result.tables)
              .filter(([, rs]) => (rs ?? []).length > 0)
              .map(([name, rs]) => (
                <div key={name}>
                  <p className="gst-sub mb-2">
                    {TABLE_LABELS[name as AmendmentTable] ?? name.toUpperCase()}
                  </p>
                  <Rows rows={rs!} />
                </div>
              ))}

          {(result.filed_not_in_books?.length ?? 0) > 0 && (
            <>
              <p className="gst-sub mb-2">Filed, and not in this period&rsquo;s books — left alone</p>
              <ul className="gst-meta mb-3 list-disc pl-5">
                {result.filed_not_in_books!.map((d) => (
                  <li key={`${d.section}|${d.ctin}|${d.doc_no}`}>
                    {d.section.toUpperCase()} · {d.doc_no ?? '—'}
                    {d.ctin ? ` · ${d.ctin}` : ''} · taxable {rupees(d.taxable)}
                  </li>
                ))}
              </ul>
            </>
          )}

          {(result.new_in_this_period?.length ?? 0) > 0 && (
            <p className="gst-meta mb-2">
              {result.new_in_this_period!.length} document
              {result.new_in_this_period!.length === 1 ? ' is' : 's are'} in these books and not in
              that filing — new supplies of this period, reported in the ordinary tables.
            </p>
          )}

          {unbuiltSentences(result).map((line) => (
            <p key={line} className="gst-meta">
              {line}
            </p>
          ))}

          {keptSentence(body) && <p className="gst-meta mt-2">{keptSentence(body)}</p>}
        </div>
      )}
    </Card>
  )
}
